import { LitElement, html, css } from 'lit';
import { FetchMixin } from '../FetchMixin';
import './DashboardLoadingOverlay.js';
import alertStore from '../AlertStore.js';
import { setAlert } from '../AlertSlice.js';
import './DashboardAppList.js';

class DashboardArchiveContent extends FetchMixin(LitElement) {
  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .section {
      background: transparent;
      margin-bottom: 24px;
      opacity: 1;
      transition: opacity 0.4s ease, transform 0.4s ease;
    }

    .btn-back {
      background: transparent;
      border: none;
      color: #6b7280;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 0;
      margin-bottom: 16px;
      transition: color 0.2s;
    }

    .btn-back:hover {
      color: #111827;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding: 0 8px;
    }

    .section-header h1 {
      font-size: 1.5rem;
      font-weight: 800;
      color: #111827;
      margin: 0;
      display: flex;
      align-items: center;
    }

    .confirm-ok-button {
      background-color: #ef4444;
      color: white;
      border: none;
      padding: 10px 16px;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    .confirm-ok-button:hover { background-color: #dc2626; }
    
    .confirm-cancel-button {
      background-color: #f3f4f6;
      color: #374151;
      border: none;
      padding: 10px 16px;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    .confirm-cancel-button:hover { background-color: #e5e7eb; }
    
    .confirm-restore-button {
      background-color: #10b981;
      color: white;
      border: none;
      padding: 10px 16px;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    .confirm-restore-button:hover { background-color: #059669; }

    .caution { font-size: 40px; display: flex; margin: 0 0 16px 0; justify-content: center; color: #f59e0b; }
    .question { font-size: 40px; display: flex; margin: 0 0 16px 0; justify-content: center; color: #9ca3af; }

    .archive_caution {
      margin: 0 8px 16px 8px;
      font-size: 13px;
      color: #6b7280;
    }

    /* Danger Zone Banner */
    .danger-zone-banner {
      display: flex;
      align-items: center;
      gap: 16px;
      margin: 0 8px 24px 8px;
      padding: 16px;
      border: 1px solid #fca5a5;
      border-radius: 8px;
      background: #fef2f2;
    }
    .danger-zone-banner i {
      font-size: 24px;
      color: #ef4444;
    }
    .danger-zone-banner-text strong {
      display: block;
      color: #991b1b;
      font-size: 14px;
      margin-bottom: 4px;
    }
    .danger-zone-banner-text span {
      color: #dc2626;
      font-size: 13px;
    }
  `;

  static properties = {
    identKey: { type: String },
    archivedApps: { type: Array },
    archivedVersions: { type: Array },
    loading: { type: Boolean },
    dailyReportLastTime: { type: Date },
    serverTime: { type: Date },
  };

  constructor() {
    super();
    this.identKey = null;
    this.archivedApps = [];
    this.archivedVersions = [];
    this.loading = false;
  }

  firstUpdated() {
    requestAnimationFrame(() => {
      const fontAwasomeStyle = document.querySelector("link[rel='stylesheet'][data-name='fontawesomev6']");
      if (fontAwasomeStyle) {
        const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
        fontAwasomeStyleClone.removeAttribute('disabled');
        this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);
      }
    })
  }

  async showArchiveList() {
    this.loading = true;
    try {
      await this.fetchArchiveList();
    } catch (error) {
      console.error('Error fetching archive list:', error);
    } finally {
      this.loading = false;
    }
  }

  async fetchArchiveList() {
    try {
      const json = await this.fetchWithAuth(process.env.ARCHIVE_LIST_ENDPOINT, { method: 'GET' });
      if (json.details.identKey) {
        this.identKey = json.details.identKey;
        this.dispatchEvent(new CustomEvent('changeIdentKey', { detail: { identKey: this.identKey }, bubbles: true, composed: true }));
      }
      this.archivedApps = json.details.applications;
      this.archivedVersions = json.details.versions;
      return json;
    } catch (e) {
      return Promise.reject(e.message);
    }
  }

  goBack() {
    this.dispatchEvent(new CustomEvent('switch-view', { detail: { view: 'home' }, bubbles: true, composed: true }));
  }

  async showRestoreAppConfirmMessageBox(appKey) {
    const messageBox = this.shadowRoot.querySelector('#restore-confirm');
    const ret = await messageBox.show();
    if (ret) this.restoreApp(appKey);
    messageBox.hide();
  }

  _handleAppAction(e) {
    const { action, appKey } = e.detail;
    if (action === 'restore') this.showRestoreAppConfirmMessageBox(appKey);
    else if (action === 'delete') this.showDeleteAppConfirmMessageBox(appKey);
  }

  render() {
    return html`
    <div class="section app-management">

        <message-box id="delete-confirm">
            <div slot="message-slot">
                <p class="caution"><i class="fa-solid fa-triangle-exclamation"></i></p>
                <p id="delete-confirm-msg" style="text-align: center; font-weight: bold;"></p>
            </div>
            <button slot="ok-slot" class="confirm-ok-button">削除する</button>
            <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
        </message-box>

        <message-box id="restore-confirm">
            <div slot="message-slot">
                <p class="question"><i class="fa-solid fa-circle-question"></i></p>
                <p style="text-align: center; font-weight: bold;">アーカイブからワークベンチに復元しますか？<br><span style="font-size:0.85em; color:#6b7280; font-weight:normal;">※１ＰＰを使用します</span></p>
            </div>
            <button slot="ok-slot" class="confirm-restore-button">復元</button>
            <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
        </message-box>
        
      <button class="btn-back" @click=${this.goBack}><i class="fa-solid fa-arrow-left"></i> ワークベンチに戻る</button>

      <div class="section-header">
        <h1>アーカイブ一覧</h1>
      </div>
      
      <p class="archive_caution"><i class="fa-solid fa-circle-info" style="color:#10b981;"></i> アーカイブにあるアプリケーションは１つあたり1日で１ＰＰを使用します</p>
      
      ${this.archivedApps && this.archivedApps.length > 0 ? html`
      <div class="danger-zone-banner">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <div class="danger-zone-banner-text">
          <strong>警告：完全削除について</strong>
          <span>アーカイブからアプリを削除すると、すべてのバージョンとデータが完全に消去され、二度と復元できません。</span>
        </div>
      </div>
      ` : ''}

      ${this.loading ? html`<dashboard-loading-overlay></dashboard-loading-overlay>` : ''}

      <div>
        <dashboard-app-list 
          mode="archive" 
          .apps=${this.archivedApps} 
          .versions=${this.archivedVersions} 
          .identKey=${this.identKey}
          @app-action=${this._handleAppAction}
        ></dashboard-app-list>
      </div>

    </div>
    `;
  }

  async restoreApp(appKey) {
    let options = { method: 'PUT', body: JSON.stringify({ appKey: appKey }) }
    this.loading = true;
    try {
      await this.fetchWithAuth(process.env.APPLICATION_RESTORE_ENDPOINT, options);
      alertStore.dispatch(setAlert(`アプリ「${appKey}」を復元しました`));
    } catch (e) {
      console.error('Error:', e.message);
    } finally {
      this.loading = false;
      this.showArchiveList();
    }
    this.dispatchEvent(new CustomEvent('changeAppStatus', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
  }

  async deleteApp(appKey) {
    let options = { method: 'DELETE', body: JSON.stringify({ appKey: appKey }) }
    this.loading = true;
    try { await this.fetchWithAuth(process.env.APPLICATION_ENDPOINT, options); }
    catch (e) { console.error('Error:', e.message); }
    finally { this.loading = false; this.showArchiveList(); }
    this.dispatchEvent(new CustomEvent('changeAppStatus', { bubbles: true, composed: true }));
  }

  async showDeleteAppConfirmMessageBox(appKey) {
    if (!this.archivedVersions || !Array.isArray(this.archivedVersions)) {
      alertStore.dispatch(setAlert('バージョン情報を確認できません。削除処理を中止しました。'));
      return;
    }
    const versionsForApp = this.archivedVersions.filter(version => version.appKey === appKey);
    const hasPublishedVersion = versionsForApp.some(version => version.status === "published");

    if (hasPublishedVersion) {
      alertStore.dispatch(setAlert('公開中のバージョンが存在するアプリケーションは削除できません。'));
    } else {
      const app = this.archivedApps.find(a => a.appKey === appKey);
      const appName = app ? app.appName : appKey;

      const messageBox = this.shadowRoot.querySelector('#delete-confirm');
      const msgEl = messageBox.querySelector('#delete-confirm-msg');
      msgEl.innerHTML = `アプリ「<span style="color:#ef4444;">${appName}</span>」を<br>完全に削除しますか？<br><span style="font-size: 0.8em; color: #ef4444; margin-top: 8px; display: block;">※この操作は取り消せません</span>`;

      const okButton = messageBox.querySelector('.confirm-ok-button');
      okButton.onclick = async () => {
        this.deleteApp(appKey);
        messageBox.hide();
      };
      messageBox.show();
    }
  }
}

customElements.define('dashboard-archive-content', DashboardArchiveContent);